import type { ReactNode } from 'react';
import { Alert, Button, Input } from 'antd';
import { SaveOutlined, UndoOutlined } from '@ant-design/icons';
import ReactSplit from 'react-split';
import HeartLoader from '@/shared/ui/HeartLoader';
import type { Template } from '@/models/template';
import type { WorksheetEditorState } from '../hooks/useWorksheetEditorState';
import { useEditorResizableLayout } from '../hooks/useEditorResizableLayout';
import { WorksheetSidebar } from './WorksheetSidebar';
import { WorksheetCanvas } from './WorksheetCanvas';
import styles from './WorksheetEditor.module.css';
import './primitives/draggable.module.css';

interface EditorShellProps<T extends Template> {
  editor: WorksheetEditorState<T>;
  isLoading: boolean;
  hasError: boolean;
  loadErrorTitle: string;
  isSaving: boolean;
  onSave: () => void;
  /** Extra column to the right of the editor. */
  aside?: ReactNode;
}

/** Header, sidebar and canvas shared by the worksheet and report template editors. */
export function EditorShell<T extends Template>({
  editor,
  isLoading,
  hasError,
  loadErrorTitle,
  isSaving,
  onSave,
  aside,
}: EditorShellProps<T>) {
  const { splitProps } = useEditorResizableLayout();
  const { worksheet, unsavedChanges, updateName, resetToServer } = editor;

  if (isLoading) {
    return (
      <div className={styles.container}>
        <div className={styles.loading}>
          <HeartLoader showText />
        </div>
      </div>
    );
  }

  if (hasError || !worksheet) {
    return (
      <div className={styles.container}>
        <Alert type="error" title={loadErrorTitle} showIcon />
      </div>
    );
  }

  const split = (
    <ReactSplit {...splitProps} className={styles.split}>
      <div className={styles.sidebarColumn}>
        <WorksheetSidebar
          worksheet={worksheet}
          onSectionsChange={editor.updateSections}
          onContextGroupsChange={editor.updateContextGroups}
          onMeasurementGroupsChange={editor.updateMeasurementGroups}
          onFindingGroupsChange={editor.updateFindingGroups}
          onFlagGroupsChange={editor.updateFlagGroups}
          onFollowUpGroupsChange={editor.updateFollowUpGroups}
        />
      </div>
      <div className={styles.canvasColumn}>
        <WorksheetCanvas
          worksheet={worksheet}
          onContextGroupsChange={editor.updateContextGroups}
          onMeasurementGroupsChange={editor.updateMeasurementGroups}
          onFindingGroupsChange={editor.updateFindingGroups}
          onFlagGroupsChange={editor.updateFlagGroups}
          onFollowUpGroupsChange={editor.updateFollowUpGroups}
        />
      </div>
    </ReactSplit>
  );

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <Input
          value={worksheet.name}
          onChange={(e) => updateName(e.target.value)}
          className={styles.nameInput}
          size="large"
        />
        <div className={styles.actions}>
          <Button
            icon={<UndoOutlined />}
            onClick={resetToServer}
            disabled={!unsavedChanges}
          >
            Discard
          </Button>
          <Button
            type="primary"
            icon={<SaveOutlined />}
            onClick={onSave}
            loading={isSaving}
            disabled={!unsavedChanges}
          >
            Save
          </Button>
        </div>
      </div>

      {aside ? (
        <div className={styles.withAside}>
          <div className={styles.editorColumn}>{split}</div>
          {aside}
        </div>
      ) : (
        split
      )}
    </div>
  );
}
